import React, { useEffect } from 'react';
import store, { searchActions, loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import MasterNotesTable from './tables/MasterNotesTable';
import SubNotesTable from './tables/SubNotesTable';
import StandardLoanTable from './tables/StandardLoanTable';
import { LoanPageType, LoanStatusType, LoanPageOptions } from './common/LoanConstants';
import { getStatusTypeName, getLoanTypeForPageType } from './common/LoanFunctions';
import { Accordion, BfiCustomStore, SectionHeader } from '../common/SharedComponents';
import { BorrowerModals } from '../borrowers/common/BorrowerComponents';
import debounce from 'lodash.debounce';
import { LoanModals } from './common/LoanComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

function LoansLandingPageGeneric({ pageType = LoanPageType.Landing, statusId = LoanStatusType.Approved }) {
    const searchState = useSelector((state) => state.searchState);
    const { dispatch } = store;

    const standardLoanGridStore = new BfiCustomStore();
    const masterNotesGridStore = new BfiCustomStore();
    const subNotesGridStore = new BfiCustomStore();
    const debouncedSearch = debounce(dispatch, 500);

    const _onSearchChange = (event) => {
        debouncedSearch(
            searchActions.setSearchText({
                searchText: {
                    ...searchState.searchText,
                    loan: event.target.value,
                },
            })
        );
    };

    useEffect(() => {
        if (!pageType) {
            return;
        }

        return () => {
            //Cancel any further searches if component is unloaded
            debouncedSearch.cancel();
        };
    }, []);

    const addLoan = (pageType) => {
        if (pageType === LoanPageType.LOC) {
            dispatch(
                loanActions.setEditMasterNoteModal({
                    editMasterNoteModal: {
                        isOpen: true,
                        loanId: 0,
                        showInsurance: false,
                    },
                })
            );
        } else {
            dispatch(
                loanActions.setEditStdLoanModal({
                    editStdLoanModal: {
                        isOpen: true,
                        loanId: 0,
                        showInsurance: false,
                        loanType: getLoanTypeForPageType(pageType),
                    },
                })
            );
        }
    };

    const headerTitle = () => {
        if (pageType === LoanPageType.Landing) {
            return getStatusTypeName(statusId);
        } else {
            return (
                <span>
                    {getStatusTypeName(statusId)}
                    <span>
                        {' '}
                        - <b>{LoanPageOptions[pageType].name}</b>
                    </span>
                </span>
            );
        }
    };

    const buttonText = () => {
        if (pageType === LoanPageType.Landing) return undefined;

        if (pageType === LoanPageType.LOC) return 'Add Master Note';

        return 'Add Loan';
    };

    if (pageType === LoanPageType.Landing) {
        return <div>"LoansLandingPageGeneric cannot be used with pageType = LoanPageType.Landing)"</div>;
    }

    return (
        <>
            <SectionHeader
                title={headerTitle()}
                buttonText={buttonText()}
                buttonCallback={() => addLoan(pageType)}
            />
            <div
                className='main-content'
                style={{ padding: 0, backgroundColor: '#f4f4f4' }}
            >
                <div className='input-has-icon main-search'>
                    <input
                        type='search'
                        placeholder='Search on BFI Number, Loan Number, City, State, Borrower or User...'
                        defaultValue={searchState?.searchText?.loan}
                        onChange={(event) => _onSearchChange(event)}
                    />
                    <FontAwesomeIcon
                        icon={faSearch}
                        aria-hidden='true'
                        className='icon'
                    />
                </div>

                <div
                    className='scrollable-content'
                    style={{ padding: '1rem' }}
                >
                    <div
                        className='loans accordion'
                        data-accordion=''
                        data-allow-all-closed='true'
                        data-multi-expand='true'
                    >
                        {pageType !== LoanPageType.LOC && (
                            <Accordion
                                title={getStatusTypeName(statusId)}
                                isExpanded={true}
                                //Set the id so that the accordian resets state when the user moves to a different Loan Page
                                id={`acc_Loan_SL_${getLoanTypeForPageType(pageType)}_${statusId}`}
                            >
                                <div className='card'>
                                    <div className='card-section'>
                                        <StandardLoanTable
                                            loanTypeId={getLoanTypeForPageType(pageType)}
                                            statusId={statusId}
                                            gridStore={standardLoanGridStore}
                                        />
                                    </div>
                                </div>
                            </Accordion>
                        )}
                        {pageType === LoanPageType.LOC && (
                            <>
                                <Accordion
                                    title={'Master Notes'}
                                    isExpanded={true}
                                    //Set the id so that the accordian resets state when the user moves to a different Loan Page
                                    id={`acc_Loan_MN_${statusId}`}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            <MasterNotesTable
                                                statusId={statusId}
                                                gridStore={masterNotesGridStore}
                                            />
                                        </div>
                                    </div>
                                </Accordion>
                                <Accordion
                                    title={'Sub Notes'}
                                    isExpanded={true}
                                    //Set the id so that the accordian resets state when the user moves to a different Loan Page
                                    id={`acc_Loan_SN_${statusId}`}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            <SubNotesTable
                                                statusId={statusId}
                                                gridStore={subNotesGridStore}
                                            />
                                        </div>
                                    </div>
                                </Accordion>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <LoanModals />
            <BorrowerModals />
        </>
    );
}

export default LoansLandingPageGeneric;
